import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  IconButton,
  Stack,
  Tooltip,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  Typography,
  Autocomplete,
  Divider,
} from '@mui/material';
import { ThumbUp, Edit, Add, Delete } from '@mui/icons-material';
import { GridColDef, GridFilterModel, GridOverlay } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { EmojiFlagMap, EmojiFlagMapType } from '../../../services/partnerResourcesHelpers';

import moment from 'moment';

import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../../utils/adminDashboardUtils';

import BaseToolbar from '../../Datagrid/BaseToolbar';
import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';

import fetchPartnerResources, {
  deletePartnerResource,
  fetchResourceCategories,
  patchPartnerResource,
  patchPartnerResourceImages,
  postPartnerResource,
  RESOURCE_CATEGORY_TITLES,
} from '../../../services/partnerResources';

import {
  PartnerResource,
  PartnerResourceCategory,
  ResourceImageType,
} from '../../../interfaces/partnerResources.interface';

import Flag from '../../PartnerResources/PartnerResourcesFlags';
import { getOrderedWorkflowTypes, workflowShortVersion } from '../../../services/workflow';
import { useWorkflowMap } from '../../../hooks/useWorkflowMap';
import PartnerResourcesModal from '../../PartnerResources/PartnerResourcesModal';
import { fileNameFromUrl } from '../../../utils/fileUtils';

const BestPracticeTranslations = () => {
  const workflowMap = useWorkflowMap();
  const workflowTypeKeys = useMemo(() => getOrderedWorkflowTypes(), [workflowMap]);
  const [rows, setRows] = useState<PartnerResource[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [loading, setLoading] = useState(true);
  const [bestPracticesCategoryId, setBestPracticesCategoryId] = useState<number | null>(null);
  const [showAddPreview, setShowAddPreview] = useState(false);
  const [newDocPreviewUrl, setNewDocPreviewUrl] = useState<string | null>(null);
  const [newTranslationModalOpen, setNewTranslationModalOpen] = useState(false);
  const [newFlagOrLanguage, setNewFlagOrLanguage] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newVersionKey, setNewVersionKey] = useState<string>(getOrderedWorkflowTypes()[0] ?? '');
  const [newDocFile, setNewDocFile] = useState<File | null>(null);
  const [addError, setAddError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTranslations, setEditTranslations] = useState<PartnerResource[]>([]);
  const [selectedVersionKey, setSelectedVersionKey] = useState<string>('');
  const [editFlagOrLanguage, setEditFlagOrLanguage] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editDocFile, setEditDocFile] = useState<File | null>(null);
  const [editDocPreviewUrl, setEditDocPreviewUrl] = useState<string | null>(null);

  const [isDeleteAllVersionsOpen, setIsDeleteAllVersionsOpen] = useState(false);
  const [deletingRows, setDeletingRows] = useState<PartnerResource[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteBPVersionOpen, setIsDeleteBPVersionOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState<PartnerResource | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [showPRCPreview, setShowPRCPreview] = useState(false);

  const allFlaggedLanguages = Object.keys(EmojiFlagMap) as EmojiFlagMapType[];

  const selectedTranslation = useMemo(() => {
    if (!selectedVersionKey) return null;
    return editTranslations.find((t) => t.description === selectedVersionKey) ?? null;
  }, [editTranslations, selectedVersionKey]);

  function CustomToolbar(props: { openInviteAdminModal: () => void }) {
    const { openInviteAdminModal, ...rest } = props;

    return (
      <BaseToolbar {...rest} csvOptions={{ fileName: `best_practice_translations_${moment().format('MM_DD_YYYY')}` }}>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={() => setNewTranslationModalOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Translation
          </Button>
        </Box>
      </BaseToolbar>
    );
  }

  const fetchBestPracticeTranslations = useCallback(
    async (categoryId: number) => {
      setLoading(true);
      try {
        const { count, results } = await fetchPartnerResources({
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
          ordering:
            sortModel.length > 0
              ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}`
              : undefined,
          filterModel: filterModelApplied,
          categoryId,
        });

        setRows(results);
        setRowCount(count);
      } catch (error) {
        console.error('Error fetching best practice translations:', error);
      } finally {
        setLoading(false);
      }
    },
    [paginationModel, sortModel, filterModelApplied],
  );

  const handleFetchResourceCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { results } = await fetchResourceCategories();

      const categoryId = results.find(
        (cat: PartnerResourceCategory) => cat.title === RESOURCE_CATEGORY_TITLES.BEST_PRACTICES,
      )?.id;

      if (categoryId) {
        setBestPracticesCategoryId(categoryId);
        await fetchBestPracticeTranslations(categoryId);
      } else {
        setBestPracticesCategoryId(null);
        console.warn('BEST PRACTICES category not found. Cannot fetch translations.');
      }
    } catch (error) {
      console.error('Error fetching resource categories:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchBestPracticeTranslations]);

  const handleOpenEdit = useCallback((row: { translations: PartnerResource[] }) => {
    const translations = (row.translations ?? []).map((t) => ({
      ...t,
      images: (t.images ?? []).map((img) => ({ ...img })),
    }));

    setEditTranslations(translations);

    const first = translations[0] ?? null;
    setSelectedVersionKey(first?.description ?? workflowTypeKeys[0] ?? '');
    setEditFlagOrLanguage(first?.title ?? '');
    setEditDisplayName(first?.subDescription ?? '');
    setEditImageFile(null);
    setEditDocFile(null);
    setEditDocPreviewUrl(null);
    setEditError('');
    setIsEditOpen(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    if (isSaving) return;

    setIsEditOpen(false);
    setEditTranslations([]);
    setSelectedVersionKey('');
    setEditFlagOrLanguage('');
    setEditDisplayName('');
    setEditError('');
    setEditImageFile(null);
    setEditDocFile(null);
    setEditDocPreviewUrl(null);
  }, [isSaving]);


  const handleCloseDelete = useCallback(() => {
    if (isDeleting) return;

    setIsDeleteAllVersionsOpen(false);
    setIsDeleteBPVersionOpen(false);
    setDeletingRow(null);
    setDeletingRows(null);
  }, [isDeleting]);

  const handleDeleteAllVersionsConfirm = useCallback(async () => {
    if (!deletingRows || deletingRows.length === 0) {
      setEditError('No translations selected for deletion.');
    }

    setIsDeleting(true);
    try {
      await Promise.all(deletingRows!.map((t) => deletePartnerResource(Number(t.id))));
      await fetchBestPracticeTranslations(bestPracticesCategoryId!);
    } catch (error) {
      console.error('Error deleting translations:', error);
    } finally {
      setIsDeleting(false);
      handleCloseDelete();
    }
  }, [deletingRows, handleCloseDelete, bestPracticesCategoryId, fetchBestPracticeTranslations]);

  const deleteBestPracticeTranslation = useCallback(async (translation: PartnerResource) => {
    if (!translation.id) {
      setEditError('Translation is missing an ID. Cannot delete.');
      return;
    }
    setIsDeleting(true);
    setEditError('');
    console.log('Deleting translation with ID:', translation);

    try {
      await deletePartnerResource(Number(translation.id));
      const updatedTranslations = editTranslations.filter((t) => t.id !== translation.id);
      setEditTranslations(updatedTranslations);
      await fetchBestPracticeTranslations(bestPracticesCategoryId!);
    } catch (error) {
      console.error('Error deleting translation:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteBPVersionOpen(false);
    }
  }, [editTranslations]);

  const handleSaveEdit = useCallback(async () => {
    if (!selectedVersionKey) return;

    const hasNewVerionSelected = editTranslations.every(t => t.description !== selectedVersionKey);

    if (hasNewVerionSelected && !editDocFile) {
      setEditError('Document is required for new versions.');
      return;
    }

    if (!editFlagOrLanguage.trim()) {
      setEditError('Flag / Language is required.');
      return;
    }

    if (!editDisplayName.trim()) {
      setEditError('Display Name is required.');
      return;
    }

    if (selectedTranslation && !selectedTranslation.id) {
      setEditError('Selected translation is missing an ID. Cannot save.');
      return;
    }

    setIsSaving(true);
    try {
      setEditError('');

      // Ensure translation exists for this version
      let translationToSave = selectedTranslation;

      if (hasNewVerionSelected) {
        // New version selected that doesn't have a translation yet
        const base = editTranslations[0];
        await postPartnerResource(
          {
            category: Number(base?.category?.id),
            author: Number(base?.author?.id),
            title: editFlagOrLanguage,
            sub_description: editDisplayName,
            description: selectedVersionKey,
          },
          null,
          editDocFile,
        );
      }

      if (editDocFile && translationToSave?.id) {
        await Promise.all(
          editTranslations.map((t) => patchPartnerResourceImages(Number(t.id), null, editDocFile)),
        );
      }

      await Promise.all(
        editTranslations.map((t) => patchPartnerResource(Number(t.id), {
          title: editFlagOrLanguage,
          subDescription: editDisplayName,
        })),
      );

      if (bestPracticesCategoryId) {
        await fetchBestPracticeTranslations(bestPracticesCategoryId);
      }
      handleCloseEdit();
      setShowSaved(true);
    } catch (error) {
      console.error('Error updating translation:', error);
      setEditError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedVersionKey,
    bestPracticesCategoryId,
    selectedTranslation,
    editTranslations,
    editFlagOrLanguage,
    editDisplayName,
    editImageFile,
    editDocFile,
    rows,
    handleCloseEdit,
  ]);

  const handleShowDeleteVersions = useCallback((row: { translations: PartnerResource[] }) => {
    setDeletingRows(row.translations);
    setIsDeleteAllVersionsOpen(true);
  }, []);

  const handleShowDeleteVersion = useCallback((row: PartnerResource) => {
    setDeletingRow(row);
    setIsDeleteBPVersionOpen(true);
  }, []);

  const handleCloseAdd = useCallback(() => {
    if (isAdding) return;

    setNewTranslationModalOpen(false);
    setNewFlagOrLanguage('');
    setNewDisplayName('');
    setNewVersionKey(workflowTypeKeys[0] ?? '');
    setNewDocFile(null);
    setAddError('');
  }, [isAdding]);

  const handleAddTranslation = useCallback(async () => { 
    if (!newDocFile) {
      setAddError('Document is required.');
      return;
    }
    if (!newFlagOrLanguage.trim()) {
      setAddError('Flag / Language is required.');
      return;
    }

    if (!newDisplayName.trim()) {
      setAddError('Display Name is required.');
      return;
    }
    if (!newVersionKey) {
      setAddError('Questionnaire Version is required.');
      return;
    }

    setAddError('');
    setIsAdding(true);
    try {
      if (!bestPracticesCategoryId) throw new Error('Missing BEST PRACTICES category.');
      const authorId = rows[0]?.author?.id;
      if (!authorId) throw new Error('Missing author id (cannot create).');

      const duplicate = rows.some(
        (r) => r.title === newFlagOrLanguage && r.description === newVersionKey,
      );
      if (duplicate) {
        setAddError('That language already has a translation for this version.');
        return;
      }

      await postPartnerResource(
        {
          category: bestPracticesCategoryId,
          author: authorId,
          title: newFlagOrLanguage,
          sub_description: newDisplayName,
          description: newVersionKey,
        },
        null,
        newDocFile,
      );

      await fetchBestPracticeTranslations(bestPracticesCategoryId);

      setNewTranslationModalOpen(false);
      setNewFlagOrLanguage('');
      setNewDisplayName('');
      setNewVersionKey(workflowTypeKeys[0] ?? '');
      setNewDocFile(null);
      setShowSaved(true);
    } catch (e) {
      console.error(e);
      setAddError('Failed to add translation. Please try again.');
    } finally {
      setIsAdding(false);
    }
  }, [
    newFlagOrLanguage,
    newDisplayName,
    newVersionKey,
    newDocFile,
    bestPracticesCategoryId,
    rows,
    fetchBestPracticeTranslations,
  ]);

  // Grouped by title
  const formattedRows = useMemo(() => {
    return rows.reduce(
      (
        acc: Record<
        string,
        {
          updatedAt: string;
          title: string;
          flag: string;
          subDescription: string;
          translations: PartnerResource[];
        }
        >,
        resource,
      ) => {
        const { title, updatedAt } = resource;

        if (!acc[title]) {
          acc[title] = {
            flag: title,
            title,
            subDescription: resource.subDescription,
            updatedAt: updatedAt || '',
            translations: [resource],
          };
        } else {
          acc[title].translations.push(resource);
          acc[title].updatedAt =
            acc[title].updatedAt < (updatedAt || '') ? updatedAt || '' : acc[title].updatedAt;
        }

        return acc;
      },
      {},
    );
  }, [rows]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'flag',
        headerName: 'Flag',
        filterOperators: stringContainsOnly,
        width: 100,
        renderCell: (params) => <Flag language={params.value} />,
        align: 'center',
        headerAlign: 'center',
        disableColumnMenu: true,
        filterable: false,
        sortable: false,
      },
      {
        field: 'title',
        headerName: 'Language',
        filterOperators: stringContainsOnly,
        width: 220,
        valueGetter: (params) => params.row.title,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'sub_description',
        headerName: 'Display Name',
        filterOperators: stringContainsOnly,
        width: 350,
        valueGetter: (params) => params.row.subDescription,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'questionnaire_versions',
        headerName: 'Questionnaire Versions',
        filterOperators: stringContainsOnly,
        width: 300,
        filterable: false,
        sortable: false,
        valueGetter: (params) => {
          const list: PartnerResource[] = params.row.translations ?? [];
          return list
            .map(
              (t: PartnerResource) =>
                workflowShortVersion(t.description || ''),
            )
            .join(', ');
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Last Updated',
        width: 180,
        valueFormatter: (params) => moment(params.value).format('MM/DD/YYYY'),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 110,
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(params.row);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Versions">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowDeleteVersions(params.row);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleOpenEdit, handleShowDeleteVersion],
  );

  const existingLanguages = useMemo(() => {
    return new Set(rows.map((r) => r.title).filter(Boolean));
  }, [rows]);


  const versionOptions = useMemo(() => {
    const keys = workflowTypeKeys;

    const existingByKey = new Map<string, PartnerResource>();
    for (const t of editTranslations) {
      if (t.description) existingByKey.set(t.description, t);
    }

    return keys.map((key) => {
      const existing = existingByKey.get(key) ?? null;
      const docUrl = existing?.images?.find((img) => img.type === ResourceImageType.DOCUMENT)?.image || '';
      return {
        key,
        label: workflowShortVersion(key),
        exists: Boolean(existing && existing.id),
        hasDoc: Boolean(docUrl),
        docUrl,
        translation: existing,
      };
    });
  }, [editTranslations]);

  const selectedDocUrl = useMemo(() => {
    const existing = selectedTranslation?.images?.find((img) => img.type === ResourceImageType.DOCUMENT)?.image || '';
    return editDocPreviewUrl ?? existing ?? '';
  }, [selectedTranslation, editDocPreviewUrl]);

  const selectedDocName = useMemo(() => {
    if (editDocFile?.name) return editDocFile.name;
    return fileNameFromUrl(selectedDocUrl);
  }, [editDocFile, selectedDocUrl]);

  const availableLanguages = useMemo(() => {
    return allFlaggedLanguages.filter(
      (lang) => !existingLanguages.has(lang),
    );
  }, [existingLanguages, allFlaggedLanguages]);

  const previewRows = useMemo(() => {
    if (!selectedVersionKey) return [];

    const tmpEditTranslations: PartnerResource[] = editTranslations.map((t) => ({
      ...t,
      title: editFlagOrLanguage,
      subDescription: editDisplayName,
      images: (t.images ?? []).map((img) => ({ ...img })),
    }));

    if (selectedVersionKey && editTranslations.every((t) => t.description !== selectedVersionKey)) {
      tmpEditTranslations.push({
        id: -1,
        title: editFlagOrLanguage,
        subDescription: editDisplayName,
        description: selectedVersionKey,
        category: editTranslations[0]?.category || null,
        metadata: editTranslations[0]?.metadata || null,
        author: editTranslations[0]?.author || null,
        images: editDocPreviewUrl
          ? [
            {
              type: ResourceImageType.DOCUMENT,
              image: editDocPreviewUrl,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              resource: -1,
            },
          ]
          : (editTranslations[0]?.images ?? []).filter((img) => img.type !== ResourceImageType.DOCUMENT),
      });
    }

    const nextTranslations = tmpEditTranslations.map((t) => {
      if (t.description !== selectedVersionKey) return t;

      if (!editDocPreviewUrl) return t;

      const nonDoc = (t.images ?? []).filter((img) => img.type !== ResourceImageType.DOCUMENT);
      return {
        ...t,
        images: [
          ...nonDoc,
          {
            type: ResourceImageType.DOCUMENT,
            resource: t.id || -1,
            image: editDocPreviewUrl,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
      };
    });

    return [
      ...rows.filter((r) => editTranslations.every((et) => Number(et.id) !== Number(r.id))),
      ...nextTranslations,
    ];
  }, [selectedVersionKey, rows, editTranslations, editDisplayName, editFlagOrLanguage, editDocPreviewUrl]);

  const addPreviewRows = useMemo(() => {
    if (!newTranslationModalOpen) return rows;

    if (!newFlagOrLanguage.trim() || !newDisplayName.trim() || !newVersionKey) return rows;

    const base = rows[0];
    const draft: PartnerResource = {
      id: -1,
      title: newFlagOrLanguage,
      subDescription: newDisplayName,
      description: newVersionKey,
      category: base?.category ?? { id: bestPracticesCategoryId, title: RESOURCE_CATEGORY_TITLES.BEST_PRACTICES },
      metadata: base?.metadata ?? null,
      author: base?.author ?? null,
      images: newDocPreviewUrl
        ? [
          {
            type: ResourceImageType.DOCUMENT,
            image: newDocPreviewUrl,
            resource: -1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ]
        : [],
    };

    const dup = rows.some((r) => r.title === newFlagOrLanguage && r.description === newVersionKey);
    if (dup) return rows;

    return [...rows, draft];
  }, [
    rows,
    newTranslationModalOpen,
    newFlagOrLanguage,
    newDisplayName,
    newVersionKey,
    newDocPreviewUrl,
    bestPracticesCategoryId,
  ]);

  useEffect(() => {
    if (!newDocFile) {
      setNewDocPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(newDocFile);
    setNewDocPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newDocFile]);

  useEffect(() => {
    if (!editDocFile) {
      setEditDocPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(editDocFile);
    setEditDocPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [editDocFile]);

  useEffect(() => {
    handleFetchResourceCategories();
  }, [handleFetchResourceCategories]);

  useEffect(() => {
    if (bestPracticesCategoryId) {
      fetchBestPracticeTranslations(bestPracticesCategoryId);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filterModelApplied,
    bestPracticesCategoryId,
    fetchBestPracticeTranslations,
  ]);

  return (
    <div>
      <Snackbar
        open={showSaved}
        autoHideDuration={4000}
        onClose={() => setShowSaved(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSaved(false)} icon={<ThumbUp fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
          Saved successfully
        </Alert>
      </Snackbar>

      <Card sx={{ padding: '2em' }}>
        <DataGridPro
          loading={loading}
          style={{ height: 400, width: '100%' }}
          rows={formattedRows ? Object.values(formattedRows) : []}
          columns={columns}
          getRowId={(row) => row.title}
          pagination
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          rowCount={rowCount}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onSortModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(model);
          }}
          onPageChange={(newPage: number) => {
            setPaginationModel((prev) => ({ ...prev, page: newPage }));
          }}
          onPageSizeChange={(newPageSize: number) => {
            setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }));
          }}
          onFilterModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            const normalized = normalizeFilterModel(model);
            if (!sameModel(normalized, filterModelApplied)) {
              setFilterModelApplied(normalized);
            }
          }}
          rowsPerPageOptions={[50, 100]}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'pre-line',
              lineHeight: '1.2',
              textAlign: 'left',
            },
            '& .MuiDataGrid-pinnedColumns': { boxShadow: 'none' },
            '& .MuiDataGrid-pinnedColumnHeaders': { boxShadow: 'none' },
          }}
          components={{
            Toolbar: CustomToolbar,
            LoadingOverlay: () => (
              <GridOverlay>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <CircularProgress />
                </div>
              </GridOverlay>
            ),
          }}
          componentsProps={{
            toolbar: {
              openFilterModal: () => {},
              filterModelDraft,
              setFilterModelDraft,
              handleFetchVerificationData: () => {},
            },
          }}
          initialState={{
            pinnedColumns: { right: ['actions'] },
          }}
        />

        <StandardDialog
            title="Add Best Practice Translation"
            subtitle="Create a new translation for a language and questionnaire version."
            handleClose={handleCloseAdd}
            isOpen={newTranslationModalOpen}
            >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Autocomplete
                    options={availableLanguages}
                    getOptionLabel={(option) => `${EmojiFlagMap[option] || ''} ${option}`}
                    value={newFlagOrLanguage}
                    onChange={(event, newValue) => setNewFlagOrLanguage(newValue || '')}
                    renderInput={(params) => (
                        <TextField {...params} label="Flag / Language" fullWidth disabled={isAdding} />
                    )}
                    />

                <TextField
                label="Display Name"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                fullWidth
                disabled={isAdding}
                />

                <Select
                value={newVersionKey}
                onChange={(e) => setNewVersionKey(String(e.target.value))}
                fullWidth
                disabled={isAdding}
                >
                {workflowTypeKeys.map((key) => (
                    <MenuItem key={key} value={key}>
                    {`Version - ${workflowShortVersion(key)}`}
                    </MenuItem>
                ))}
                </Select>

                <Button variant="outlined" component="label" disabled={isAdding}>
                {newDocFile ? 'Replace Document' : 'Upload Document'}
                <input
                    hidden
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setNewDocFile(e.target.files?.[0] ?? null)}
                />
                </Button>

                {newDocFile && (
                <Typography variant="caption" color="text.secondary">
                    Selected document: {newDocFile.name}
                </Typography>
                )}

                {addError && (
                <Alert severity="error" onClose={() => setAddError('')}>
                    {addError}
                </Alert>
                )}
            </Box>

            <StandardDialogActions>
                <Button onClick={handleCloseAdd} disabled={isAdding}>Close</Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    disabled={
                    isAdding ||
                    !newFlagOrLanguage.trim() ||
                    !newDisplayName.trim() ||
                    !newVersionKey ||
                    !newDocFile
                    }
                    onClick={() => setShowAddPreview(true)}
                    sx={{ mr: 1 }}
                >
                    Live Preview
                </Button>
                <AsyncSaveButton onClick={handleAddTranslation} variant="contained" isSaving={isAdding} label="Save" />
              </StandardDialogActions>
            </StandardDialog>

        {/* Edit Modal */}
        <StandardDialog
          isOpen={isEditOpen}
          title={`Edit Translation - ${editFlagOrLanguage || selectedTranslation?.title || ''}`}
          subtitle="Update the flag/language, display name, and replace the image/document for the selected questionnaire version."
          handleClose={handleCloseEdit}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedDocUrl ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      {editDocFile ? 'Selected Document' : 'Current Document'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{selectedDocName || 'Document'}</Typography>
                      <a href={selectedDocUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                        <Typography variant="body2" color="primary" component="span">
                          Open
                        </Typography>
                      </a>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No document uploaded yet.
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button variant="outlined" component="label" disabled={isSaving}>
                    {selectedDocUrl ? 'Replace Document' : 'Upload Document'}
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => setEditDocFile(e.target.files?.[0] ?? null)}
                    />
                  </Button>
                  { selectedDocUrl && !editDocFile && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => handleShowDeleteVersion(selectedTranslation as PartnerResource)}
                    >
                      Delete Version
                    </Button>
                  )}
                </Box>

                {editDocFile && (
                  <Typography variant="caption" color="text.secondary">
                    New document: {editDocFile.name}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Version dropdown shows ALL versions + doc status */}
            <Select
              value={selectedVersionKey}
              onChange={(e) => {
                const nextKey = String(e.target.value);
                setSelectedVersionKey(nextKey);
                setEditError('');
                setEditImageFile(null);
                setEditDocFile(null);
                setEditDocPreviewUrl(null);
              }}
              disabled={isSaving}
              fullWidth
              sx={{ flex: 1 }}
            >
              {versionOptions.map((v) => (
                <MenuItem key={v.key} value={v.key}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span>{`Version - ${v.label}`}</span>
                      {v.hasDoc ? (
                        <Typography variant="caption" color="success.main">
                            Document attached
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="warning.main">
                            No document
                        </Typography>
                      )}
                    </Box>
                    {!v.exists && (
                      <Typography variant="caption" color="text.secondary">
                        Will be created on save with the above flag/language and display name
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                These fields are shared across all translations for this language. Changing them will update all versions for this language.
            </Typography>

            <Autocomplete
              options={allFlaggedLanguages}
              getOptionLabel={(option) => `${EmojiFlagMap[option] || ''} ${option}`}
              value={editFlagOrLanguage}
              onChange={(event, newValue) => setEditFlagOrLanguage(newValue || '')}
              renderInput={(params) => <TextField {...params} label="Flag / Language" fullWidth disabled={isSaving} />}
            />

            <TextField
              label="Display Name"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              fullWidth
              disabled={isSaving}
              multiline
              minRows={1}
            />

            {editError && (
              <Alert severity="error" onClose={() => setEditError('')}>
                {editError}
              </Alert>
            )}
          </Box>

          <StandardDialogActions>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
              <Button onClick={handleCloseEdit} disabled={isSaving}>
                Close
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button variant="outlined" onClick={() => setShowPRCPreview(true)} disabled={isSaving} sx={{ mr: 1 }}>
                Live Preview
              </Button>
              <AsyncSaveButton variant="contained" onClick={handleSaveEdit} isSaving={isSaving} label="Save" />
            </Box>
          </StandardDialogActions>
        </StandardDialog>

        { /* Delete version Confirmation */}
        <StandardDialog
          isOpen={Boolean(isDeleteBPVersionOpen)}
          title="Delete Version?"
          handleClose={() => setIsDeleteBPVersionOpen(false)}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {
                (deletingRow && editTranslations.length === 1) ? (
                    <Box>
                        This is the only version for <strong>{editFlagOrLanguage}</strong>. If you want to delete this version, you will need to delete the entire translation by clicking the delete icon on the main table.
                    </Box>
                ) : (
            <Box>
              Are you sure you want to delete the <strong>{workflowShortVersion(selectedVersionKey)} version</strong> for <strong>{editFlagOrLanguage}</strong>? This action cannot be undone.
            </Box>
                )
            }

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={() => setIsDeleteBPVersionOpen(false)} disabled={isDeleting}>
                Close
              </Button>
              <AsyncSaveButton
                onClick={() => deleteBestPracticeTranslation(deletingRow as PartnerResource)}
                isSaving={isDeleting}
                disabled={(deletingRow && editTranslations.length === 1) || isDeleting}
                label="Delete Version"
                variant='contained'
              />
            </Box>
          </Box>
        </StandardDialog>

        {/* Delete Confirmation on All Versions */}
        <StandardDialog isOpen={isDeleteAllVersionsOpen} title="Delete All Versions?" handleClose={() => handleCloseDelete()}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              Are you sure you want to delete <strong>{ deletingRows && deletingRows?.length > 0 ? deletingRows[0].title : ''}</strong> and all its versions? This action cannot be undone.
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={() => handleCloseDelete()} disabled={isDeleting}>
                Close
              </Button>
              <AsyncSaveButton onClick={() => handleDeleteAllVersionsConfirm()} isSaving={isDeleting} label="Delete All Versions" />
            </Box>
          </Box>
        </StandardDialog>
      </Card>

      <PartnerResourcesModal
        partnerResources={showAddPreview ? addPreviewRows : (previewRows || [])}
        showResourceModal={showAddPreview || showPRCPreview}
        preventUrlChange
        previewCategory={RESOURCE_CATEGORY_TITLES.BEST_PRACTICES}
        handleCloseResourceModal={() => {
          setShowAddPreview(false);
          setShowPRCPreview(false);
        }}
        fetchResources={() => {
          if (bestPracticesCategoryId) fetchBestPracticeTranslations(bestPracticesCategoryId);
        }}
        />
    </div>
  );
};

export default BestPracticeTranslations;